import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import {
  Check, Zap, Shield, ArrowRight, Layout,
  Star, Loader2, Sparkles, Crown, Users, RefreshCw,
  Lock, Headphones, TrendingUp, X
} from "lucide-react";
import { Logo } from "../components/Logo";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_CONFIG = {
  basis: {
    icon: Zap,
    accent: "indigo",
    bg: "bg-white",
    border: "border-slate-200",
    selectedBorder: "border-indigo-500",
    iconBg: "bg-indigo-50 text-indigo-600",
    checkColor: "text-indigo-500",
    btnClass: "bg-slate-900 hover:bg-slate-800",
    badge: null,
  },
  pro: {
    icon: Shield,
    accent: "indigo",
    bg: "bg-white",
    border: "border-indigo-500",
    selectedBorder: "border-indigo-600",
    iconBg: "bg-indigo-100 text-indigo-600",
    checkColor: "text-indigo-500",
    btnClass: "bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-500/25",
    badge: "Beliebteste Wahl",
  },
  premium: {
    icon: Crown,
    accent: "amber",
    bg: "bg-gradient-to-b from-slate-900 to-slate-800",
    border: "border-slate-700",
    selectedBorder: "border-amber-500",
    iconBg: "bg-amber-400/20 text-amber-400",
    checkColor: "text-amber-400",
    btnClass: "bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold shadow-lg shadow-amber-500/25",
    badge: "Sorglos-Paket",
  },
};

const COMPARE_ROWS = [
  { label: "Professionelle Landingpage", basis: true, pro: true, premium: true },
  { label: "SSL & DSGVO-konform", basis: true, pro: true, premium: true },
  { label: "Mobile-optimiert", basis: true, pro: true, premium: true },
  { label: "Content-Updates / Monat", basis: "3", pro: "5", premium: "10" },
  { label: "Automatische Backups", basis: false, pro: "Wöchentlich", premium: "Täglich" },
  { label: "Local-SEO", basis: "Basis", pro: "Erweitert", premium: "Premium" },
  { label: "A/B-Tests & Mini-CRM", basis: false, pro: true, premium: true },
  { label: "Marketing-Automation", basis: false, pro: false, premium: true },
  { label: "Werbekampagnen inkl.", basis: false, pro: false, premium: true },
  { label: "Individuelle Anpassungen", basis: false, pro: false, premium: true },
  { label: "Support", basis: "E-Mail", pro: "Chat", premium: "1-on-1 Beratung" },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const industry = searchParams.get("industry") || "";
  const pageId = searchParams.get("page_id") || "";

  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [showCompare, setShowCompare] = useState(false);

  useEffect(() => {
    fetchPricing();
  }, [industry]);

  const fetchPricing = async () => {
    try {
      const params = industry ? `?industry=${encodeURIComponent(industry)}` : "";
      const res = await axios.get(`${API}/pricing${params}`);
      setPricing(res.data);
    } catch (err) {
      console.error("Pricing fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async (packageId) => {
    if (pageId) {
      navigate(`/kasse/${pageId}?preselect=${packageId}`);
    } else {
      navigate("/assistent");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  const packages = pricing?.packages || [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="px-4 md:px-8 py-6 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2"
            data-testid="pricing-logo"
          >
            <Logo size="lg" clickable={false} />
          </button>
          <Button
            variant="outline"
            onClick={() => navigate("/assistent")}
            className="rounded-full"
            data-testid="pricing-wizard-btn"
          >
            Kostenlos testen
          </Button>
        </div>
      </header>

      <main className="px-4 md:px-8 py-12 md:py-20">
        <div className="max-w-6xl mx-auto">
          {/* Hero */}
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-4" data-testid="pricing-headline">
              {pricing?.intro || "Wähle dein Paket"}
            </h1>
            <p className="text-base md:text-lg text-slate-500 max-w-xl mx-auto">
              {pricing?.subtitle || "Alle Pakete monatlich kündbar – inklusive Hosting & Wartung."}
            </p>
          </motion.div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-5 lg:gap-6 items-start" data-testid="pricing-cards">
            {packages.map((pkg, idx) => {
              const config = TIER_CONFIG[pkg.id] || TIER_CONFIG.basis;
              const Icon = config.icon;
              const isPro = pkg.id === "pro";
              const isPremium = pkg.id === "premium";

              return (
                <motion.div
                  key={pkg.id}
                  data-testid={`pricing-card-${pkg.id}`}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className={`
                    relative rounded-2xl border-2 flex flex-col overflow-hidden
                    ${isPremium ? config.bg : "bg-white"}
                    ${isPro ? "border-indigo-500 shadow-xl shadow-indigo-100/60 md:-mt-4 md:mb-0" : isPremium ? config.border : config.border}
                    ${isPro ? "ring-1 ring-indigo-500/20" : ""}
                    transition-all duration-300 hover:shadow-lg
                  `}
                >
                  {/* Badge */}
                  {config.badge && (
                    <div className={`text-center py-2.5 text-sm font-semibold ${
                      isPro ? "bg-indigo-600 text-white" : "bg-amber-400/20 text-amber-300"
                    }`}>
                      <div className="flex items-center justify-center gap-1.5">
                        <Star className="w-3.5 h-3.5 fill-current" />
                        {config.badge}
                      </div>
                    </div>
                  )}

                  <div className={`p-7 flex flex-col flex-1 ${!config.badge ? "pt-8" : ""}`}>
                    {/* Header with icon */}
                    <div className="mb-5">
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${config.iconBg}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <h3 className={`text-xl font-bold mb-1 ${isPremium ? "text-white" : "text-slate-900"}`}>
                        {pkg.name}
                      </h3>
                      <p className={`text-sm font-medium ${isPremium ? "text-amber-400" : "text-indigo-600"}`}>
                        {pkg.headline}
                      </p>
                    </div>

                    {/* Price */}
                    <div className="mb-2">
                      <div className="flex items-baseline gap-1">
                        <span className={`text-5xl font-bold tracking-tight ${isPremium ? "text-white" : "text-slate-900"}`}>
                          {pkg.price.toFixed(0)}
                        </span>
                        <span className={`text-lg ${isPremium ? "text-slate-400" : "text-slate-400"}`}>€</span>
                        <span className={`text-sm ${isPremium ? "text-slate-400" : "text-slate-400"}`}>/{pkg.interval}</span>
                      </div>
                    </div>

                    {/* Description */}
                    <p className={`text-sm mb-6 leading-relaxed ${isPremium ? "text-slate-300" : "text-slate-500"}`}>
                      {pkg.description}
                    </p>

                    {/* CTA Button */}
                    <Button
                      data-testid={`pricing-cta-${pkg.id}`}
                      onClick={() => handleCheckout(pkg.id)}
                      disabled={checkoutLoading === pkg.id}
                      className={`w-full h-12 rounded-xl font-semibold text-base mb-7 ${config.btnClass}`}
                    >
                      {checkoutLoading === pkg.id ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          {pkg.cta}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>

                    {/* Features */}
                    <ul className="space-y-3 flex-1">
                      {pkg.features.map((feature, fIdx) => (
                        <li key={fIdx} className="flex items-start gap-3">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            isPremium ? "bg-amber-400/20" : "bg-indigo-50"
                          }`}>
                            <Check className={`w-3 h-3 ${config.checkColor}`} />
                          </div>
                          <span className={`text-sm ${isPremium ? "text-slate-300" : "text-slate-600"}`}>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Social proof + trust */}
          <motion.div
            className="mt-14 space-y-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            {/* Trust banner */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 max-w-2xl mx-auto">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Shield className="w-5 h-5 text-indigo-500" />
                <p className="text-sm font-semibold text-slate-800">
                  {pricing?.microcopy?.trust || "Keine Technikkenntnisse nötig – wir kümmern uns um Updates, Sicherheit & Backups."}
                </p>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-5 text-xs text-slate-500">
                <span className="flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5 text-green-500" />
                  SSL inklusive
                </span>
                <span className="flex items-center gap-1.5">
                  <RefreshCw className="w-3.5 h-3.5 text-blue-500" />
                  {pricing?.microcopy?.cancel}
                </span>
                <span className="flex items-center gap-1.5">
                  <TrendingUp className="w-3.5 h-3.5 text-indigo-500" />
                  {pricing?.microcopy?.upgrade}
                </span>
                <span className="flex items-center gap-1.5">
                  <Headphones className="w-3.5 h-3.5 text-slate-400" />
                  {pricing?.microcopy?.requests}
                </span>
              </div>
            </div>

            {/* Compare toggle */}
            <div className="text-center">
              <button
                data-testid="compare-toggle-btn"
                onClick={() => setShowCompare(!showCompare)}
                className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors px-4 py-2 rounded-full hover:bg-indigo-50"
              >
                {showCompare ? (
                  <><X className="w-4 h-4" /> Vergleich ausblenden</>
                ) : (
                  <>Alle Features im Vergleich</>
                )}
              </button>
            </div>

            {/* Comparison Table */}
            {showCompare && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="max-w-4xl mx-auto"
              >
                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm" data-testid="compare-table">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-100">
                          <th className="text-left p-4 font-medium text-slate-500 w-2/5">Feature</th>
                          <th className="text-center p-4 font-semibold text-slate-700">Starter</th>
                          <th className="text-center p-4 font-semibold text-indigo-600 bg-indigo-50/50">Pro</th>
                          <th className="text-center p-4 font-semibold text-slate-700">Premium</th>
                        </tr>
                      </thead>
                      <tbody>
                        {COMPARE_ROWS.map((row, i) => (
                          <tr key={i} className={`border-b border-slate-50 ${i % 2 === 0 ? "" : "bg-slate-50/50"}`}>
                            <td className="p-4 text-slate-700 font-medium">{row.label}</td>
                            {["basis", "pro", "premium"].map((tier) => (
                              <td key={tier} className={`p-4 text-center ${tier === "pro" ? "bg-indigo-50/30" : ""}`}>
                                {row[tier] === true ? (
                                  <Check className="w-4 h-4 text-green-500 mx-auto" />
                                ) : row[tier] === false ? (
                                  <span className="text-slate-300">—</span>
                                ) : (
                                  <span className="text-slate-700 font-medium">{row[tier]}</span>
                                )}
                              </td>
                            ))}
                          </tr>
                        ))}
                        {/* Price row */}
                        <tr className="bg-slate-50 border-t border-slate-200">
                          <td className="p-4 font-bold text-slate-900">Monatlich</td>
                          <td className="p-4 text-center font-bold text-slate-900">29€</td>
                          <td className="p-4 text-center font-bold text-indigo-600 bg-indigo-50/50">49€</td>
                          <td className="p-4 text-center font-bold text-slate-900">79€</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </main>
    </div>
  );
}
